import React from 'react';
import isEmpty from 'lodash/isEmpty';

import { TFunction } from '@lyearn/i18n';
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
import VisibilitySettingsFormField from '@/components/Form/components/VisibilitySettings';
import { VisibilitySettingsFormFieldProps } from '@/components/Form/components/VisibilitySettings/types';
import useFile from '@/modules/tasks/views/TasksDashboard/hooks/useFile';
import {
  IconSettingsInput,
  IconTypeEnum,
  VisibilitySettingsAccessTypeEnum,
  VisibilitySettingsType,
} from '@/types/schema';

import SectionNameAndEmoji from '../SectionNameAndEmoji';

export const INITIAL_VALUES = {
  name: '',
  iconSettings: {},
  visibilitySettings: {
    accessType: VisibilitySettingsAccessTypeEnum.Open,
  } as VisibilitySettingsType,
};

export const getInitialValues = (file?: ReturnType<typeof useFile>['file']) => {
  if (!file) {
    return INITIAL_VALUES;
  }

  return {
    name: file.name,
    iconSettings: file.iconSettings,
    visibilitySettings: file.visibilitySettings as VisibilitySettingsType,
  };
};

export type TaskListEditorFormValues = ReturnType<typeof getInitialValues>;

const VISIBILITY_SETTINGS_CONTAINER_OVERRIDES: VisibilitySettingsFormFieldProps['overrides'] = {
  restrictedContent: {
    overrides: {
      label: { variant: 'body-short-02' },
      addButton: { className: 'mt-6' },
    },
  },
};

export const getFormFields = (t: TFunction): Fields<TaskListEditorFormValues> => {
  return {
    name: {
      name: 'name',
      type: FieldType.Custom,
      Component: ({ value: nameValue, setFieldValue, error, touched, onChange: onChangeName }) => {
        const iconFieldKey = 'iconSettings';
        const iconValue = (useFormFieldValue(iconFieldKey) as IconSettingsInput)?.value;

        const onChangeIcon = (nextValue: string) => {
          setFieldValue(
            iconFieldKey,
            {
              type: IconTypeEnum.Emoji,
              value: nextValue,
            },
            true,
          );
        };

        return (
          <SectionNameAndEmoji
            error={touched && error}
            icon={iconValue}
            name={nameValue}
            onChangeIcon={onChangeIcon}
            onChangeName={onChangeName}
          />
        );
      },
    },
    visibilitySettings: {
      name: 'visibilitySettings',
      label: t('VisibilitySetting:VisibleTo'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <VisibilitySettingsFormField
          overrides={VISIBILITY_SETTINGS_CONTAINER_OVERRIDES}
          type="users_and_groups"
          value={value}
          onChange={onChange}
        />
      ),
    },
  };
};

export const FORM_LAYOUT = ['name', 'visibilitySettings'];

export const validateForm = (t: TFunction) => (values: any) => {
  const errors: any = {};

  if (isEmpty(values.name)) {
    errors.name = t('Task:NameCannotBeEmpty');
  }

  return errors;
};
